import React, { useEffect, useState } from 'react';
import axios from 'axios';
import appConfig from 'services/apiConfig';

interface NotificationListProps {
    userId: any;  // userId should be a number
    role: 'Client' | 'Operator';  // role can either be 'Client' or 'Operator'
}

const NotificationList: React.FC<NotificationListProps> = ({ userId, role }) => {
    const [notifications, setNotifications] = useState<any[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    
    useEffect(() => {
        const fetchNotifications = async () => {
            setIsLoading(true);
            try {
                const response = await axios.get(`${appConfig.getBaseUrl()}/notification`, {
                    params: { userId, role }, 
                });
                console.log('Notifications received:', response.data.notifications);
                setNotifications(response.data.notifications);
            } catch (error) {
                console.error('Failed to fetch notifications:', error);
            } finally {
                setIsLoading(false);
            }
        };
        console.log('Fetching notifications for userId:', userId, 'role:', role);
        if (userId) {
            fetchNotifications();
        }
    }, [userId, role]);

    const formatDateTime = (timestamp: number) => {
        const date = new Date(timestamp * 1000);
        const today = new Date();
        const isToday = date.toDateString() === today.toDateString();
        
        if (isToday) {
            return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
        }
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    };

    return (
        <div className="chat-container mt-6 px-4"  style={{ height: '100%', overflowY: 'auto' }}>
            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-4 rounded-t-lg shadow-md">
                <h2 className="text-2xl font-bold">💬 Messages</h2>
                <p className="text-sm opacity-90">Communication between you and {role === 'Operator' ? 'your clients' : 'your operator'}</p>
            </div>
            
            {isLoading ? (
                <div className="flex justify-center items-center py-8">
                    <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
                </div>
            ) : notifications.length === 0 ? (
                <div className="bg-gray-50 p-8 rounded-b-lg shadow text-center">
                    <p className="text-gray-500 text-lg">📭 No messages yet</p>
                    <p className="text-gray-400 text-sm mt-2">Start a conversation by sending a message below</p>
                </div>
            ) : (
                <div className="messages bg-white rounded-b-lg shadow p-4 space-y-4">
                    {notifications.map((notification) => {
                        const isSentByOperator = notification.sent_by === 0;
                        const isMyMessage = role === 'Operator' ? isSentByOperator : !isSentByOperator;
                        
                        console.log('Rendering notification:', {
                            id: notification.id,
                            message: notification.send_notification,
                            sent_by: notification.sent_by,
                            isMyMessage,
                            sender_name: notification.sender_name
                        });
                        
                        return (
                            <div
                                key={notification.id}
                                className={`flex ${isMyMessage ? 'justify-end' : 'justify-start'}`}
                            >
                                <div className={`max-w-[70%] min-w-[200px] rounded-lg p-4 shadow-md ${
                                    isMyMessage 
                                        ? 'bg-blue-500' 
                                        : 'bg-gray-100'
                                }`}>
                                    <div className="message-header flex justify-between items-center mb-2">
                                        <span className={`font-semibold text-sm ${isMyMessage ? 'text-white' : 'text-gray-700'}`}>
                                            {isMyMessage ? 'You' : notification.sender_name || 'Unknown'}
                                        </span>
                                        <span className={`text-xs ml-3 ${isMyMessage ? 'text-blue-100' : 'text-gray-500'}`}>
                                            {formatDateTime(notification.sent_date)}
                                        </span>
                                    </div>
                                    <div 
                                        className={`message-content break-words whitespace-pre-wrap min-h-[20px] ${
                                            isMyMessage ? 'text-gray-700' : 'text-gray-900'
                                        }`}
                                        style={{ 
                                            fontSize: '14px',
                                            lineHeight: '1.5',
                                            color: isMyMessage ? '#110d0dff' : '#111827'
                                        }}
                                    >
                                        {notification.send_notification ? notification.send_notification : '(Empty message)'}
                                    </div>
                                    {isMyMessage && (
                                        <div className="text-xs text-blue-100 mt-2 flex items-center">
                                            <span className="mr-1">✓</span> Delivered
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default NotificationList;
